import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Loader2, Eye, EyeOff, Ticket, Boxes, Mail } from 'lucide-react';
import BenDeskLogo from '../components/BenDeskLogo';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Esconde o badge flutuante "Made by BenfiTech" enquanto na tela de login.
  useEffect(() => {
    const badge = document.getElementById('benfitech-badge');
    if (!badge) return undefined;
    const previousDisplay = badge.style.display;
    badge.style.display = 'none';
    return () => {
      badge.style.display = previousDisplay;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
      navigate('/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage =
        err.response?.data?.detail || err.message || 'Usuário ou senha inválidos';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Painel hero (azul) — escondido no mobile */}
      <div className="hidden lg:flex flex-col justify-between bg-gradient-hero text-white p-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, white 0%, transparent 40%), radial-gradient(circle at 80% 80%, white 0%, transparent 35%)',
          }}
        />
        <div className="relative flex items-center justify-center flex-1">
          <BenDeskLogo
            badge
            badgeClassName="bg-gradient-to-br from-blue-500 to-blue-700 shadow-2xl ring-1 ring-white/20"
            markSize={64}
            markClassName="text-white"
            wordClassName="text-white"
            deskClassName="text-blue-500"
            tagClassName="text-white/75"
          />
        </div>
        <div className="relative space-y-6 max-w-md">
          <h1 className="text-3xl font-bold leading-tight">
            Central de Serviços de TI
            <span className="block text-2xl font-semibold text-white/90 mt-1.5">
              Chamados, ativos e estoque num só lugar.
            </span>
          </h1>
          <p className="text-white/80 leading-relaxed">
            Abra e acompanhe chamados, controle o inventário de TI e o estoque, com
            abertura automática por e-mail e histórico completo.
          </p>
          <div className="grid grid-cols-3 gap-4 pt-2">
            {[
              { icon: Ticket, label: 'Chamados' },
              { icon: Boxes, label: 'Ativos' },
              { icon: Mail, label: 'E-mail → Ticket' },
            ].map((f) => (
              <div
                key={f.label}
                className="rounded-xl bg-white/10 backdrop-blur p-4 border border-white/15"
              >
                <f.icon className="h-5 w-5 mb-2" />
                <p className="text-xs font-semibold">{f.label}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="relative text-xs text-white/60">
          © 2026 BenfiTech — Todos os direitos reservados.
        </p>
      </div>

      {/* Painel do formulário */}
      <div className="flex items-center justify-center p-6 lg:p-12 bg-background">
        <div className="w-full max-w-md rounded-2xl border border-border/60 bg-card p-8 shadow-lg-soft">
          {/* Logo só no mobile (o hero some) */}
          <div className="lg:hidden flex justify-center mb-8">
            <BenDeskLogo
              markSize={36}
              markClassName="text-white"
              badgeClassName="bg-gradient-to-br from-blue-500 to-blue-700 shadow-md"
              wordClassName="text-slate-900"
              deskClassName="text-primary"
              showTagline={false}
            />
          </div>

          <div className="space-y-2 mb-8">
            <h2 className="text-2xl font-bold text-foreground">Bem-vindo de volta</h2>
            <p className="text-sm text-muted-foreground">
              Acesse o BenDesk com seu usuário e senha.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div
                className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <label htmlFor="username" className="text-sm font-medium text-foreground">
                Usuário
              </label>
              <Input
                id="username"
                type="text"
                placeholder="Digite seu usuário"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
                data-testid="login-username-input"
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="password" className="text-sm font-medium text-foreground">
                Senha
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Digite sua senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2 text-muted-foreground">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                />
                Lembrar-me
              </label>
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Entrar'}
            </Button>
          </form>

          <p className="text-[11px] text-muted-foreground text-center mt-8">
            BenDesk © 2026 · BenfiTech.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
